/**
 * DashboardGrid
 *
 * Responsive CSS Grid layout wrapper for arranging charts and KPI cards
 * in dashboard layouts. Uses design tokens for gap spacing.
 *
 * Usage:
 *   <DashboardGrid>
 *     <DashboardGrid.Item span={2}>
 *       <BarChart data={...} />
 *     </DashboardGrid.Item>
 *     <DashboardGrid.Item>
 *       <DonutChart data={...} />
 *     </DashboardGrid.Item>
 *   </DashboardGrid>
 */

"use client";

import React, { forwardRef } from "react";
import { cn } from "@/src/utils/utils";

// ---------------------------------------------------------------------------
// Grid Container
// ---------------------------------------------------------------------------

export interface DashboardGridProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Number of columns at desktop. Default 4. */
  columns?: 1 | 2 | 3 | 4 | 6;
  /** Gap size. Default "md". */
  gap?: "sm" | "md" | "lg" | "xl";
  /** Enable staggered fade-in animation for children */
  animated?: boolean;
}

const columnClasses: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-1 md:grid-cols-2",
  3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
  4: "grid-cols-1 md:grid-cols-2 lg:grid-cols-4",
  6: "grid-cols-2 md:grid-cols-3 lg:grid-cols-6",
};

const gapClasses: Record<string, string> = {
  sm: "gap-2",
  md: "gap-4",
  lg: "gap-6",
  xl: "gap-8",
};

export const DashboardGrid = forwardRef<HTMLDivElement, DashboardGridProps>(
  function DashboardGrid(
    { columns = 4, gap = "md", animated = false, className, children, ...props },
    ref
  ) {
    return (
      <div
        ref={ref}
        className={cn(
          "grid w-full",
          columnClasses[columns],
          gapClasses[gap],
          animated && "stagger-children",
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

// ---------------------------------------------------------------------------
// Grid Item
// ---------------------------------------------------------------------------

export interface DashboardGridItemProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Number of columns this item spans. Default 1. */
  span?: 1 | 2 | 3 | 4 | 6;
  /** Number of rows this item spans. Default 1. */
  rowSpan?: 1 | 2 | 3;
}

const spanClasses: Record<number, string> = {
  1: "col-span-1",
  2: "col-span-1 md:col-span-2",
  3: "col-span-1 md:col-span-2 lg:col-span-3",
  4: "col-span-1 md:col-span-2 lg:col-span-4",
  6: "col-span-2 md:col-span-3 lg:col-span-6",
};

const rowSpanClasses: Record<number, string> = {
  1: "row-span-1",
  2: "row-span-2",
  3: "row-span-3",
};

const DashboardGridItem = forwardRef<HTMLDivElement, DashboardGridItemProps>(
  function DashboardGridItem(
    { span = 1, rowSpan = 1, className, children, ...props },
    ref
  ) {
    return (
      <div
        ref={ref}
        className={cn(
          spanClasses[span],
          rowSpanClasses[rowSpan],
          "min-h-0",
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

// ---------------------------------------------------------------------------
// Compound Component
// ---------------------------------------------------------------------------

const DashboardGridCompound = Object.assign(DashboardGrid, {
  Item: DashboardGridItem,
});

export { DashboardGridCompound as default, DashboardGridItem };
